// {{WORKTREE_ROOT}}/picosponsorconsole/app/utils/webauthn.client.ts
// PLAN2 WP10 — WebAuthn JSON (base64url) <-> ArrayBuffer, client-side only (§6.10). Uses the browser's own
// PublicKeyCredential.parse*FromJSON when present, else converts manually. Never imported from a .server.ts file.
export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window && !!navigator.credentials;
}

function b64urlToBuffer(b64url: string): ArrayBuffer {
  const pad = b64url.length % 4 === 0 ? "" : "=".repeat(4 - (b64url.length % 4));
  const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
function bufferToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function manualRequestOptions(json: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  const allow = (json.allowCredentials as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    ...(json as unknown as PublicKeyCredentialRequestOptions),
    challenge: b64urlToBuffer(String(json.challenge)),
    allowCredentials: allow.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: b64urlToBuffer(String(c.id)) })),
  };
}
function manualCreationOptions(json: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = json.user as Record<string, unknown>;
  const exclude = (json.excludeCredentials as Array<Record<string, unknown>> | undefined) ?? [];
  return {
    ...(json as unknown as PublicKeyCredentialCreationOptions),
    challenge: b64urlToBuffer(String(json.challenge)),
    user: { ...(user as unknown as PublicKeyCredentialUserEntity), id: b64urlToBuffer(String(user.id)) },
    excludeCredentials: exclude.map((c) => ({ ...(c as unknown as PublicKeyCredentialDescriptor), id: b64urlToBuffer(String(c.id)) })),
  };
}

function assertionToJson(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id, rawId: bufferToB64url(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON), authenticatorData: bufferToB64url(r.authenticatorData),
      signature: bufferToB64url(r.signature), userHandle: r.userHandle ? bufferToB64url(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  };
}
function attestationToJson(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id, rawId: bufferToB64url(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufferToB64url(r.clientDataJSON), attestationObject: bufferToB64url(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults?.() ?? {},
  };
}

export async function getAssertion(optionsJson: Record<string, unknown>): Promise<Record<string, unknown>> {
  const PK = window.PublicKeyCredential as unknown as { parseRequestOptionsFromJSON?: (j: unknown) => PublicKeyCredentialRequestOptions };
  const publicKey = PK.parseRequestOptionsFromJSON ? PK.parseRequestOptionsFromJSON(optionsJson) : manualRequestOptions(optionsJson);
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential;
  return assertionToJson(cred);
}

export async function createCredential(optionsJson: Record<string, unknown>): Promise<Record<string, unknown>> {
  const PK = window.PublicKeyCredential as unknown as { parseCreationOptionsFromJSON?: (j: unknown) => PublicKeyCredentialCreationOptions };
  const publicKey = PK.parseCreationOptionsFromJSON ? PK.parseCreationOptionsFromJSON(optionsJson) : manualCreationOptions(optionsJson);
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
  return attestationToJson(cred);
}
